import React from 'react';

// Small shared bits of chrome for the movement processor tabs, in the app's
// Atrium language (cards, pills, hairlines) rather than the standalone tool's CSS.

export function Card({ title, sub, right, children, style }) {
  return (
    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border)', borderRadius: 'var(--oh-radius-card)', boxShadow: 'var(--depth-rest)', marginBottom: 16, overflow: 'hidden', ...style }}>
      {(title || right) && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-soft)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            {title && <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-strong)' }}>{title}</div>}
            {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{sub}</div>}
          </div>
          {right}
        </div>
      )}
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  );
}

export function Badge({ kind = 'muted', children }) {
  const map = {
    ok:    { bg: 'var(--success-bg)',  fg: 'var(--success-ink)' },
    warn:  { bg: 'var(--warning-bg)', fg: 'var(--warning-ink)' },
    err:   { bg: 'var(--critical-bg)',   fg: 'var(--critical-ink)' },
    info:  { bg: 'var(--info-bg)', fg: 'var(--info-ink)' },
    muted: { bg: 'var(--hover)', fg: 'var(--text-secondary)' },
  };
  const c = map[kind] || map.muted;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 'var(--oh-radius-pill)', fontSize: 11, fontWeight: 700, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

export function Button({ variant = 'primary', children, style, ...rest }) {
  const base = { fontSize: 12.5, fontWeight: 700, padding: '9px 16px', borderRadius: 'var(--oh-radius-control)', border: 'none', cursor: rest.disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', opacity: rest.disabled ? 0.55 : 1 };
  const kinds = {
    primary: { background: 'var(--action)', color: 'var(--on-action)', boxShadow: 'none' },
    ghost:   { background: 'var(--surface-glass)', color: 'var(--action-text)', boxShadow: 'inset 0 0 0 1.5px var(--border)' },
    quiet:   { background: 'var(--hover)', color: 'var(--text-secondary)' },
    danger:  { background: 'var(--critical-bg)', color: 'var(--critical-ink)' },
  };
  return <button className="mv-btn" style={{ ...base, ...kinds[variant], ...style }} {...rest}>{children}</button>;
}

export function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 5 }}>{hint}</div>}
    </div>
  );
}

export const inputStyle = {
  width: '100%', padding: '10px 13px', borderRadius: 'var(--oh-radius-control)', border: 'none', fontSize: 13.5,
  fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--surface-data)',
  boxShadow: 'inset 0 0 0 1px var(--border-strong)', outline: 'none', boxSizing: 'border-box',
};

/*
  A cell you can type in, on the Review screen.

  **Measured for the first time on 8 September 2026**, and it was 31 pixels
  against rule 13's floor of 44. It had never been measured because the screen
  could never be REACHED: its rows only existed after a real page had been read
  off a real photograph, so the whole-app checker walked past it every time.
  Making a run pick-up-able is what found this.

  The class carries the floor rather than this object, because a media query
  cannot reach an inline style, which is the same thing that hid the row button
  and the count line's three dots until job 18.
*/
export const cellInputStyle = {
  width: '100%', padding: '6px 8px', borderRadius: 'var(--oh-radius-chip)', border: 'none', fontSize: 12.5,
  fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--surface-data)',
  boxShadow: 'inset 0 0 0 1px var(--border-strong)', outline: 'none', boxSizing: 'border-box',
};

/* Put this on anything using the style above, so the floor can reach it. */
export const CELL_CLASS = 'mv-cell';

/*
  **Six things that used to live here are gone**, removed 8 September 2026 when
  the last of the Movement screens was built to Group G's approved drawings.

  `Stat` and `StatRow` were four numbers in coloured boxes, which rule 5
  forbids; they are `Figures` and `Figure` in `src/components/Figures.jsx` now,
  on hairlines, shared with Run History. `Th` and `Td` were an HTML table, which
  rule 15 forbids; every list here is a `ListGrid`. `WarnBlock` stacked three
  warnings above two hundred rows without saying which row each was about; each
  warning is on its own row now. `EmptyState` was one grey sentence in the
  middle of nothing; it is `EmptyScreen`, which says what the screen is for and
  offers the way on.

  They are deleted rather than left, so nothing new can be built on them.
*/
